import { useMemo } from "react";

interface SystemInfo {
	cookies: {
		count: number;
		details: Array<{ name: string; value: string; domain?: string }>;
	};
	datadog: {
		traceId: string | null;
		sessionId: string | null;
		rum: {
			session_id: string | null;
			application_id: string | null;
		};
	};
	screen: {
		width: number;
		height: number;
		colorDepth: number;
		pixelRatio: number;
	};
	network: {
		online: boolean;
		effectiveType: string | null;
		downlink: number | null;
	};
	memory: {
		usedJSHeapSize: number | null;
		totalJSHeapSize: number | null;
		jsHeapSizeLimit: number | null;
	};
	timestamp: string;
	timezone: string;
	language: string;
}

export const useSystemInfo = (): SystemInfo => {
	return useMemo(() => {
		// Cookie information
		const getCookieInfo = () => {
			const cookies = document.cookie
				.split(";")
				.filter((cookie) => cookie.trim())
				.map((cookie) => {
					const [name, ...valueParts] = cookie.trim().split("=");

					return {
						name: name.trim(),
						value: valueParts.join("=").trim().substring(0, 10),
					};
				});

			return {
				count: cookies.length,
				details: cookies,
			};
		};

		// Datadog tracking information
		const getDatadogInfo = () => {
			const rum = (globalThis as any).DD_RUM;

			let sessionId = null;
			let applicationId = null;
			let traceId = null;

			// Try to get RUM session info
			try {
				if (rum?.getInternalContext) {
					const context = rum.getInternalContext();

					sessionId = context?.session_id || null;
					applicationId = context?.application_id || null;
				}
			} catch (e) {
				console.warn("Failed to get RUM context:", e);
			}

			// Try to get trace ID from cookies or local storage
			try {
				const traceCookie = document.cookie
					.split(";")
					.find((row) => row.trim().startsWith("dd-trace-id="));

				if (traceCookie) {
					traceId = traceCookie.split("=")[1];
				}
			} catch (e) {
				console.warn("Failed to get trace ID:", e);
			}

			return {
				traceId,
				sessionId,
				rum: {
					session_id: sessionId,
					application_id: applicationId,
				},
			};
		};

		// Screen information
		const getScreenInfo = () => ({
			width: screen.width,
			height: screen.height,
			colorDepth: screen.colorDepth,
			pixelRatio: window.devicePixelRatio,
		});

		// Network information
		const getNetworkInfo = () => {
			const navigator = globalThis.navigator as any;
			const connection =
				navigator.connection || navigator.mozConnection || navigator.webkitConnection;

			return {
				online: navigator.onLine,
				effectiveType: connection?.effectiveType || null,
				downlink: connection?.downlink || null,
			};
		};

		// Memory information
		const getMemoryInfo = () => {
			const memory = (performance as any).memory;

			return {
				usedJSHeapSize: memory?.usedJSHeapSize || null,
				totalJSHeapSize: memory?.totalJSHeapSize || null,
				jsHeapSizeLimit: memory?.jsHeapSizeLimit || null,
			};
		};

		return {
			cookies: getCookieInfo(),
			datadog: getDatadogInfo(),
			screen: getScreenInfo(),
			network: getNetworkInfo(),
			memory: getMemoryInfo(),
			timestamp: new Date().toISOString(),
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			language: navigator.language,
		};
	}, []);
};
